import { Injectable } from "@angular/core";
import { MassUploadEntryConfig, MassUploadEntryMap, MassUploadExcelToJsonMap, MassUploadStateFormMap, EXCEL_PERSON_TYPES, EXCEL_ROW_INDEX, EXCEL_ENROLLMENT_TYPES, MassUploadRowData, MassUploadSpouseDependentValidations, MassUploadGridConfiguration, MassUploadRowAction } from "../interfaces/mass-upload-grid.interface";
import { MassUploadGridService } from "./mass-upload-grid.service";
import moment from "moment";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { EntryConfigInterface, FormConfig, FormNavigationConfig, PtlMaskService } from "@ptl/forms";
import { MASS_UPLOAD_STATE_NAME } from "../reducer/mass-upload-grid.reducer";
import { MOMENT_PARSE_FORMAT } from "../interfaces/mug-submission.interface";
import { MassUploadGridEntryMapTransformerService } from "./mug-entry-map-transformer.service";
import { MassUploadGridCaseService } from "./mug-case.service";
import { MassUploadGridDataHelperService } from "./mug-data-helper.service";
import { MassUploadGridFormHelperService } from "./mug-form-helper.service";
import { MassUploadGridSubmissionDataService } from "./mug-submission-data.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormConfigUtilityService } from "../../../form-wrappers/services/filterByConfigMetatags/form-config-utility.service";
import { environment } from 'src/environments/environment';
import { CommonUtilityService } from "../../../../shared/utility-services/common-utility.service";
import { BaseAgency } from "@ptl/account-management";
import { BehaviorSubject } from "rxjs";


@Injectable({
  providedIn: 'root'
})
export class MassUploadGridRowActionsService {

  MAX_ROWS: number = 100;
  currentEditRowIndex: number = -1;
  gridFormMap: MassUploadStateFormMap[] = [];
  gridContentList: string[][] = [];
  gridEntryList: MassUploadEntryConfig[][] = [];
  formJsonToExcelMap: MassUploadExcelToJsonMap[] = [];
  
  openEnrollmentJson: FormConfig<FormNavigationConfig>;
  specialEnrollmentJson: FormConfig<FormNavigationConfig>;
  openEnrollmentAboutYouJson: FormConfig<FormNavigationConfig>;
  specialEnrollmentAboutYouJson: FormConfig<FormNavigationConfig>;
  allEntryMapOpenEnrollment: MassUploadEntryMap = {};
  allEntryMapSpecial: MassUploadEntryMap = {};

  loadingStatus = new BehaviorSubject<boolean>(false);
  loadingStatus$ = this.loadingStatus.asObservable();

  constructor(
    private formConfigUtilityService: FormConfigUtilityService,
    private mugService: MassUploadGridService,
    private mugDataHelperService: MassUploadGridDataHelperService,
    private mugFormHelper: MassUploadGridFormHelperService,
    private mugEntryTransformerService: MassUploadGridEntryMapTransformerService,
    private matSnackBar: MatSnackBar,
    private commonUtilityService: CommonUtilityService
  ) {
  }

  async initializeFormsAsJson(massUploadGridConfig: MassUploadGridConfiguration, agency: BaseAgency) {
    // Because we aren't storing things in previously recognized states, we have to override the meta tags for our forms mapper
    // to make sure we pull the correct forms for the correct ASP and state.
    // TODO - CAN REGIONS IN THE SAME STATE HAVE DIFFERING FORM REQUIREMENTS? 
    let metaTagOverrides = {
      asp: environment.initData.brand.ASPID,
      state: agency.region,
      userType: this.commonUtilityService.getEnrollmentFormType()
    };

    this.openEnrollmentJson = await this.formConfigUtilityService.getFormConfigFromMapAndMetatags('open-enrollment', metaTagOverrides);
    this.specialEnrollmentJson = await this.formConfigUtilityService.getFormConfigFromMapAndMetatags('special-enrollment', metaTagOverrides);
    this.openEnrollmentAboutYouJson = await this.formConfigUtilityService.getFormConfigFromMapAndMetatags('open-enrollment-about-you', metaTagOverrides);
    this.specialEnrollmentAboutYouJson = await this.formConfigUtilityService.getFormConfigFromMapAndMetatags('special-enrollment-about-you', metaTagOverrides);
    this.mugFormHelper.loadDependentValidations(this.openEnrollmentAboutYouJson, this.specialEnrollmentAboutYouJson);

    // Set an empty map so we can ignore warnings that this doesn't exist
    this.openEnrollmentJson.replacementMap = {};
    this.specialEnrollmentJson.replacementMap = {};

    
    /**
     * ::WARNING::
     * 
     * It is here that we begin the journey of losing data, validators, etc if it shares the same formControlName as any other control within its form
     *  Always, with the latest value in the forms ( those on the final pages and towards the end of the ordering ) become the only value ( due to overwrites )
     *  So while you get SOME VERSION of a formControlName and entry - they are not guaranteed to be the same
     * 
     * QUESTION: Why don't dependent controls suffer the pain of overwrites?
     * ANSWER: Dependents are isolated before flattening, so there is not likely to be shared nameing ( outside of the dependents on formControlNames )
     */
    // This pulls all of the entries out of the form config and compiles them into a map for easier lookup
    this.allEntryMapOpenEnrollment = this.mugEntryTransformerService.flattenAllEntriesToMap(this.openEnrollmentJson);
    this.allEntryMapSpecial = this.mugEntryTransformerService.flattenAllEntriesToMap(this.specialEnrollmentJson);
    this.mugFormHelper.configureEnrollmentMap(this.allEntryMapOpenEnrollment, massUploadGridConfig.entryOverrides);
    this.mugFormHelper.configureEnrollmentMap(this.allEntryMapSpecial, massUploadGridConfig.entryOverrides);
  }

  async addNewSubscriberRowOpen(index: number) {
    await this.showSpinnerAndAddSingleRow(index, EXCEL_PERSON_TYPES.SUBSCRIBER, EXCEL_ENROLLMENT_TYPES.OEP);
  }

  async addNewChildOnlyRowOpen(index: number) {
    await this.showSpinnerAndAddSingleRow(index, EXCEL_PERSON_TYPES.CHILD_ONLY, EXCEL_ENROLLMENT_TYPES.OEP);
  }

  async addNewSubscriberRowSpecial(index: number) {
    await this.showSpinnerAndAddSingleRow(index, EXCEL_PERSON_TYPES.SUBSCRIBER, EXCEL_ENROLLMENT_TYPES.SEP);
  }

  async addNewChildOnlyRowSpecial(index: number) {
    await this.showSpinnerAndAddSingleRow(index, EXCEL_PERSON_TYPES.CHILD_ONLY, EXCEL_ENROLLMENT_TYPES.SEP);
  }

  async addNewDependentRow(actionRow: number, dependentType: EXCEL_PERSON_TYPES, enrollmentType: EXCEL_ENROLLMENT_TYPES) {
    await this.showSpinnerAndAddSingleRow(actionRow, dependentType, enrollmentType);
  }

  async showSpinnerAndAddSingleRow(index: number, personType: EXCEL_PERSON_TYPES, enrollmentType: string) {
    this.loadingStatus.next(true);
    if (await this.addNewRow(index, personType, enrollmentType)) {
      await this.loadAndInitializeSingleEntryData(this.gridEntryList[index], index);
    }
    this.loadingStatus.next(false);
  }

  // Add a new row based at the index specified and initialize all components and forms 
  async addNewRow(insertIndex: number, personType: EXCEL_PERSON_TYPES, enrollmentType: string, flattenedEnrollmentData?: any): Promise<boolean> {
    if (this.gridContentList.length >= this.MAX_ROWS) {
      this.matSnackBar.open(`You have reached the maximum amount of possible rows (${this.MAX_ROWS})`, 'OK', {
        duration: 1500,
        panelClass: 'danger-panel-class'
      });
      return false;
    }

    const stateId = this.getNextStateId();

    // Create empty row data with default values
    const newRowData = new Array(this.formJsonToExcelMap.length).fill('');
    newRowData[EXCEL_ROW_INDEX.ROW_NUMBER] = insertIndex;
    newRowData[EXCEL_ROW_INDEX.PERSON_TYPE] = personType;
    newRowData[EXCEL_ROW_INDEX.ENROLLMENT_TYPE] = enrollmentType;

    await this.buildRow({ rowData: newRowData, formPatchData: flattenedEnrollmentData }, insertIndex, stateId);

    // Update grid index and edit mode for sync
    this.clearEditRowAndSyncIndex();

    return true;
  }

  async buildRow(newRowData: MassUploadRowData, insertIndex: number, stateId: number) {
    let entryMap = this.allEntryMapOpenEnrollment;

    // Make sure we are using the correct form configurations for the enrollment type provided by the data
    if (newRowData.rowData[EXCEL_ROW_INDEX.ENROLLMENT_TYPE] === EXCEL_ENROLLMENT_TYPES.SEP) {
      entryMap = this.allEntryMapSpecial;
    }

    const newEntryList = this.mugFormHelper.buildSingleRowEntryList(entryMap, newRowData, stateId);

    // Insert into grid content list
    const newFormGroups = this.mugFormHelper.toFormGroup(newEntryList);
    this.gridFormMap.splice(insertIndex, 0, { stateId: stateId, formGroups: newFormGroups });
    this.gridEntryList.splice(insertIndex, 0, newEntryList);
  }

  async loadAndInitializeSingleEntryData(entryList, index) {
    let enrollmentJson = this.openEnrollmentJson;

    // Make sure we are using the correct form configurations for the enrollment type provided by the data
    if (this.mugDataHelperService.getEnrollmentTypeByStateId(this.gridFormMap[index].stateId) === EXCEL_ENROLLMENT_TYPES.SEP) {
      enrollmentJson = this.specialEnrollmentJson;
    }

    // Preload any network data to the cache so the form components will never have to wait for responses
    await this.mugService.preloadServiceValues(entryList);
    let components = await this.mugDataHelperService.createAndInitializeComponents(enrollmentJson, entryList, this.gridFormMap[index].formGroups);

    // Wait before destroying all the components so the triggers have time to finish. Waiting also limits the amount of 
    // components triggering at any one time and will reduce the overall load and completion time for each set of triggers.
    // Because of how our form stream triggers work, every single form control will be listening to every other form control,
    // which could end up in the thousands. Without this pause, values and validation will be in a constant race condition for large sets
    await new Promise(resolve => setTimeout(resolve, 800));
    setTimeout(function () {
      for (let j = 0; j < components.length; j++) {
        components[j].destroy();
      }
    }, 800);
  }

  removeSubscriberRow(index: number) {
    let formMapItems = this.mugDataHelperService.getFullFamilyFormGroupsBySubscriberGridIndex(index);
    const numDependents = formMapItems.length - 1;

    // remove rows in reverse order so we remove the subscriber row last
    // subscriber row = index
    for (let i = 0; i <= numDependents; i++) {
      this.removeRow(index + numDependents - i);
    }
  }

  removeRow(index: number) {
    let ownerMap = this.mugDataHelperService.getOwnerFormMapFromStateId(this.gridFormMap[index].stateId);

    // remove a row from the grid. remove the form group, data row, and entry list at the given index
    this.gridFormMap.splice(index, 1);
    this.gridContentList.splice(index, 1);
    this.gridEntryList.splice(index, 1);

    // Refresh form validation for subscriber and grid scrolling after removing a row
    ownerMap.formGroups[this.mugDataHelperService.getStatusFormKey(ownerMap.stateId)].updateValueAndValidity();

    // If we removed the currently edited row, reset the edit index
    if (index === this.currentEditRowIndex) {
      this.currentEditRowIndex = -1;
    }
    // If we removed a row before the current edit row, adjust the index
    else if (index < this.currentEditRowIndex) {
      this.currentEditRowIndex--;
    }

    // Update scrollable elements and trigger change detection
    this.loadingStatus.next(false);
  }

  getNextStateId() {
    // get the largest state Id and return 1 plus that number to remain unique
    let maxStateId = 0;

    this.gridEntryList.forEach(entryList => {
      let stateId = entryList[0].massUploadAttributes.stateId;
      if (stateId && stateId > maxStateId) {
        maxStateId = stateId;
      }
    });

    return maxStateId + 1;
  }
  
  changeEditRow(index: number) {
    if (index == this.currentEditRowIndex) {
      return;
    }
    
    this.gridEntryList[index].forEach(entry => {
      entry.massUploadAttributes.editMode = true;
    });
    
    if (this.currentEditRowIndex >= 0) {
      this.gridEntryList[this.currentEditRowIndex].forEach(entry => {
        entry.massUploadAttributes.editMode = false;
      });
    }

    this.currentEditRowIndex = index;
  }

  clearEditRowAndSyncIndex() {
    if (this.currentEditRowIndex == -1) {
      return;
    }

    this.currentEditRowIndex = -1;
    for (let i = 0; i < this.gridEntryList.length; i++) {
      this.gridEntryList[i].forEach((entry) => {
        entry.massUploadAttributes.editMode = false;
      });
    }
  }

  getUpdatedRowActions(rowIndex: number, $event: Event): MassUploadRowAction[] {
    $event.preventDefault();
    $event.stopPropagation();
    let actionMenuItems: MassUploadRowAction[] = [];

    const personType = this.mugDataHelperService.getPersonTypeByStateId(this.gridFormMap[rowIndex].stateId);

    if (personType === EXCEL_PERSON_TYPES.SUBSCRIBER) {
      const allForms = this.mugDataHelperService.getFullFamilyFormGroupsBySubscriberGridIndex(rowIndex);
      const enrollmentType = this.mugDataHelperService.getEnrollmentTypeByStateId(this.gridFormMap[rowIndex].stateId);
      const configuredEnrollmentTypes = this.getAllConfiguredEnrollmentTypes();
      let spouseFormGroups = [];
      let depFormGroups = [];

      allForms.forEach((gridFormMap) => {
        const personType = this.mugDataHelperService.getPersonTypeByStateId(gridFormMap.stateId);
        if (personType === EXCEL_PERSON_TYPES.SPOUSE) {
          spouseFormGroups.push(gridFormMap.formGroups);
        }
        else if (personType === EXCEL_PERSON_TYPES.DEPENDENT) {
          depFormGroups.push(gridFormMap.formGroups);
        }
      });

      if (spouseFormGroups.length < this.mugFormHelper.getSpouseValidations(enrollmentType).maxSpousalPartners) {
        actionMenuItems.push({
          id: 'addSpouse',
          label: 'Add Spouse',
          callback: () => { this.addNewDependentRow(rowIndex + 1, EXCEL_PERSON_TYPES.SPOUSE, enrollmentType) }
        });
      }

      if (depFormGroups.length < this.mugFormHelper.getDependentValidations(enrollmentType).maxDependents) {
        actionMenuItems.push({
          id: 'addDependent',
          label: 'Add Dependent',
          callback: () => { this.addNewDependentRow(rowIndex + 1, EXCEL_PERSON_TYPES.DEPENDENT, enrollmentType) }
        });
      }

      if (this.gridContentList.length < this.MAX_ROWS) {
        let insertIndex = rowIndex + spouseFormGroups.length + depFormGroups.length + 1;

        if (configuredEnrollmentTypes.indexOf(EXCEL_ENROLLMENT_TYPES.OEP) != -1) {
          actionMenuItems.push({
            id: 'addSubOEP',
            label: 'Insert OEP Subscriber Below',
            callback: () => { this.addNewSubscriberRowOpen(insertIndex) }
          });
          actionMenuItems.push({
            id: 'addChildOEP',
            label: 'Insert OEP Child-Only Below',
            callback: () => { this.addNewChildOnlyRowOpen(insertIndex) }
          });
        }

        if (configuredEnrollmentTypes.indexOf(EXCEL_ENROLLMENT_TYPES.SEP) != -1) {
          actionMenuItems.push({
            id: 'addSubSEP',
            label: 'Insert SEP Subscriber Below',
            callback: () => { this.addNewSubscriberRowSpecial(insertIndex) }
          });
          actionMenuItems.push({
            id: 'addChildSEP',
            label: 'Insert SEP Child-Only Below',
            callback: () => { this.addNewChildOnlyRowSpecial(insertIndex) }
          });
        }
      }

      actionMenuItems.push({
        id: 'deleteRow',
        label: 'Remove Row and Dependents',
        callback: () => { this.removeSubscriberRow(rowIndex) }
      });
    }
    else {
      actionMenuItems.push({
        id: 'deleteRow',
        label: 'Remove Row',
        callback: () => { this.removeRow(rowIndex) }
      });
    }

    return actionMenuItems;
  }

  getAllConfiguredEnrollmentTypes(): EXCEL_ENROLLMENT_TYPES[] {
    let enrollmentTypes = [];
    let enrollmentTypeEntry = this.gridEntryList[0].filter((entry) => entry.formControlAttributes?.formControlName === 'enrollmentType')[0];
    enrollmentTypeEntry?.radioGroupAttributes?.optionAttributes.forEach((option) => {
      enrollmentTypes.push(option.value);
    });

    return enrollmentTypes;
  }
}